import { supabase } from '../lib/supabase';
import { getActiveSeason } from '../config/seasons';

/**
 * Registration Service
 * Handles all Supabase database operations for individual dancer registration
 */

// =====================================================
// REGISTRATION OPERATIONS
// =====================================================

/**
 * Create a new registration (with idempotency check)
 * This prevents duplicate registrations by checking if one already exists
 */
export const createRegistration = async (userId) => {
  const year = getActiveSeason().year;

  // First, check if user already has a registration for this year
  const existingDraft = await getActiveDraftRegistration(userId, year);

  if (existingDraft) {
    console.log('⚠️ User already has an active draft registration:', existingDraft.id);
    return existingDraft;
  }

  // Check for ANY existing registration (including submitted/paid)
  const { data: anyExisting, error: checkError } = await supabase
    .from('registrations')
    .select('*')
    .eq('user_id', userId)
    .eq('registration_type', 'individual')
    .eq('competition_year', year)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (checkError) throw checkError;

  if (anyExisting) {
    console.log(`⚠️ User already has a registration for ${year}:`, anyExisting.id, 'Status:', anyExisting.status);
    return anyExisting;
  }

  // No existing registration, create a new one
  const { data, error } = await supabase
    .from('registrations')
    .insert({
      user_id: userId,
      registration_type: 'individual',
      competition_year: year,
      status: 'draft',
      current_step: 1
    })
    .select()
    .single();

  if (error) {
    // If we get a unique constraint violation, fetch and return the existing registration
    if (error.code === '23505') { // PostgreSQL unique violation error code
      console.log('⚠️ Unique constraint violation, fetching existing registration');
      const existing = await getActiveDraftRegistration(userId, year);
      if (existing) return existing;
      
      // Fallback: get any registration
      const { data: fallback } = await supabase
        .from('registrations')
        .select('*')
        .eq('user_id', userId)
        .eq('registration_type', 'individual')
        .eq('competition_year', year)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (fallback) return fallback;
    }
    throw error;
  }
  
  return data;
};

/**
 * Get user's registrations
 */
export const getUserRegistrations = async (userId) => {
  const { data, error } = await supabase
    .from('registrations')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

/**
 * Get a specific registration by ID
 */
export const getRegistrationById = async (registrationId) => {
  const { data, error } = await supabase
    .from('registrations')
    .select('*')
    .eq('id', registrationId)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update registration
 */
export const updateRegistration = async (registrationId, updates) => {
  const { data, error } = await supabase
    .from('registrations')
    .update(updates)
    .eq('id', registrationId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete registration (draft only)
 */
export const deleteRegistration = async (registrationId) => {
  const { error } = await supabase
    .from('registrations')
    .delete()
    .eq('id', registrationId)
    .eq('status', 'draft');

  if (error) throw error;
};

/**
 * Get user's most recent draft registration
 */
export const getActiveDraftRegistration = async (userId, year = getActiveSeason().year) => {
  // Scoped to the season AND to individual registrations. Without the type
  // filter a teacher's draft *student* row can come back here (school student
  // rows carry the teacher's user_id); without the year filter an abandoned
  // draft from a previous season would be resumed instead of starting a new one.
  const { data, error } = await supabase
    .from('registrations')
    .select('*')
    .eq('user_id', userId)
    .eq('registration_type', 'individual')
    .eq('competition_year', year)
    .eq('status', 'draft')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

// =====================================================
// DANCER INFORMATION OPERATIONS
// =====================================================

/**
 * Save dancer information
 */
export const saveDancerInformation = async (registrationId, dancerInfo) => {
  // Check if record exists
  const { data: existing } = await supabase
    .from('dancer_information')
    .select('id')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (existing) {
    // Update existing
    const { data, error } = await supabase
      .from('dancer_information')
      .update(dancerInfo)
      .eq('registration_id', registrationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    // Insert new
    const { data, error } = await supabase
      .from('dancer_information')
      .insert({ registration_id: registrationId, ...dancerInfo })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/**
 * Get dancer information
 */
export const getDancerInformation = async (registrationId) => {
  const { data, error } = await supabase
    .from('dancer_information')
    .select('*')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * The dancer info from this user's most recent individual registration in an
 * earlier season, for Step 1's "reuse my info" offer. Returns
 * { year, dancerInfo } or null.
 */
export const getPreviousDancerInfo = async (userId, beforeYear) => {
  const { data, error } = await supabase
    .from('registrations')
    .select('competition_year, dancer_information(*)')
    .eq('user_id', userId)
    .eq('registration_type', 'individual')
    .lt('competition_year', beforeYear)
    .order('competition_year', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  const match = (data || []).find(r => {
    const di = Array.isArray(r.dancer_information) ? r.dancer_information[0] : r.dancer_information;
    return di?.first_name;
  });
  if (!match) return null;
  const dancerInfo = Array.isArray(match.dancer_information) ? match.dancer_information[0] : match.dancer_information;
  return { year: match.competition_year, dancerInfo };
};

// =====================================================
// CONSENT OPERATIONS
// =====================================================

/**
 * Save consent responses
 */
export const saveConsents = async (registrationId, consents) => {
  // Check if record exists
  const { data: existing } = await supabase
    .from('registration_consents')
    .select('id')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (existing) {
    // Update existing
    const { data, error } = await supabase
      .from('registration_consents')
      .update(consents)
      .eq('registration_id', registrationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    // Insert new
    const { data, error } = await supabase
      .from('registration_consents')
      .insert({ registration_id: registrationId, ...consents })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/**
 * Get consent responses
 */
export const getConsents = async (registrationId) => {
  const { data, error } = await supabase
    .from('registration_consents')
    .select('*')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

// =====================================================
// VARIATION OPERATIONS
// =====================================================

/**
 * Save variation
 */
export const saveVariation = async (registrationId, variationNumber, variationData) => {
  // Check if record exists
  const { data: existing } = await supabase
    .from('variations')
    .select('id')
    .eq('registration_id', registrationId)
    .eq('variation_number', variationNumber)
    .maybeSingle();

  if (existing) {
    // Update existing
    const { data, error } = await supabase
      .from('variations')
      .update(variationData)
      .eq('registration_id', registrationId)
      .eq('variation_number', variationNumber)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    // Insert new
    const { data, error } = await supabase
      .from('variations')
      .insert({
        registration_id: registrationId,
        variation_number: variationNumber,
        ...variationData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/**
 * Get all variations for a registration
 */
export const getVariations = async (registrationId) => {
  const { data, error } = await supabase
    .from('variations')
    .select('*')
    .eq('registration_id', registrationId)
    .order('variation_number');

  if (error) throw error;
  return data || [];
};

/**
 * Delete a variation
 */
export const deleteVariation = async (registrationId, variationNumber) => {
  const { error } = await supabase
    .from('variations')
    .delete()
    .eq('registration_id', registrationId)
    .eq('variation_number', variationNumber);

  if (error) throw error;
};

// =====================================================
// COMPLETE REGISTRATION DATA
// =====================================================

/**
 * Get complete registration data with all related information
 */
export const getCompleteRegistration = async (registrationId) => {
  const [registration, dancerInfo, consents, variations] = await Promise.all([
    getRegistrationById(registrationId),
    getDancerInformation(registrationId),
    getConsents(registrationId),
    getVariations(registrationId)
  ]);

  return {
    registration,
    dancerInfo,
    consents,
    variations
  };
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

// Age divisions live in src/config/categories.js; re-exported for existing callers.
export { getAgeDivision, isMinor } from '../config/categories';
